import type { Blockquote } from 'mdast';
import type { State } from '../state';

/** Matches `[!NOTE]` (possibly with trailing newline) at the start of a text node. */
const alertTextPattern = /^\[!(\w+)\]\n?/u;
/** Matches `[!NOTE]` as the entire value of an html node (as emitted by mdast-util-from-pm). */
const alertHtmlPattern = /^\[!(\w+)\]$/u;

/** Map GFM alert types to ADF panel types (inverse of panelTypeToAlert in mdast-util-from-pm). */
const ALERT_TO_PANEL: Record<string, string> = {
	NOTE: 'note',
	TIP: 'success',
	IMPORTANT: 'info',
	WARNING: 'warning',
	CAUTION: 'error',
};

/**
 * Detect a GFM alert tag in the first child of a blockquote.
 *
 * Supports two formats:
 * 1. Legacy / raw GFM: the alert tag is the prefix of the first text node
 *    in the first paragraph,
 *    e.g. `{ type: 'paragraph', children: [{ type: 'text', value: '[!NOTE]\nBody' }] }`
 * 2. Serialised by mdast-util-from-pm: the alert tag is a block-level `html`
 *    node as the first direct child of the blockquote,
 *    e.g. `{ type: 'html', value: '[!NOTE]' }` followed by further block children.
 *
 * Returns `{ alertType, contentChildren }` or `null` if not an alert.
 */
function detectAlert(
	node: Blockquote,
): { alertType: string; contentChildren: Blockquote['children'] } | null {
	const firstChild = node.children[0];

	// Format 2: block-level html node (from mdast-util-from-pm)
	if (firstChild?.type === 'html') {
		const match = alertHtmlPattern.exec(firstChild.value);
		if (match) {
			return {
				alertType: match[1].toUpperCase(),
				contentChildren: node.children.slice(1),
			};
		}
	}

	if (firstChild?.type !== 'paragraph') {
		return null;
	}

	// Format 1: alert tag as prefix of first text node
	const firstInline = firstChild.children[0];
	if (firstInline?.type === 'text') {
		const match = alertTextPattern.exec(firstInline.value);
		if (match) {
			const remaining = firstInline.value.slice(match[0].length);
			const newFirstChildren =
				remaining.length > 0
					? [{ ...firstInline, value: remaining }, ...firstChild.children.slice(1)]
					: firstChild.children.slice(1);
			const contentChildren: Blockquote['children'] =
				newFirstChildren.length > 0
					? [{ ...firstChild, children: newFirstChildren }, ...node.children.slice(1)]
					: node.children.slice(1);
			return { alertType: match[1].toUpperCase(), contentChildren };
		}
	}

	return null;
}

/**
 * Handler for mdast blockquote nodes.
 * Converts GFM alert blockquotes (e.g. [!NOTE]) to ADF panel nodes when the schema supports them,
 * and falls back to plain blockquote nodes otherwise.
 */
export function blockquote(state: State, node: Blockquote): void {
	const alert = detectAlert(node);
	if (alert) {
		const panelType = ALERT_TO_PANEL[alert.alertType];
		const panelNodeType =
			state.schema.nodes.panel ?? state.schema.nodes.note ?? state.schema.nodes.callout;

		if (panelType && panelNodeType) {
			state.openNode(panelNodeType, { panelType });
			state.parseTokens(
				alert.contentChildren.length > 0 ? alert.contentChildren : node.children.slice(1),
			);
			state.closeNode();
			return;
		}
	}

	// Regular blockquote
	const nodeType = state.schema.nodes.blockquote ?? state.schema.nodes.block_quote;
	if (!nodeType) {
		// Gracefully degrade: schema has no blockquote node — render children directly
		state.parseTokens(node.children);
		return;
	}
	state.openNode(nodeType);
	state.parseTokens(node.children);
	state.closeNode();
}
